import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { hasKey } from '~/utils';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import {
  PostAdditionalDocumentRequestBody,
  PostAdditionalDocumentRequestResponse,
  AdditionalDocumentsRequest,
} from '@ab/ams-school-api/types/private/v1';
import { GetCustomizedDocumentTypesResponse } from '@ab/ams-school-api/src/services/studentDocuments/v1/studentDocuments/types';

export function usePostAdditionalDocumentRequest(id: string) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const { isLoading, error, mutateAsync } = useMutation(
    ['application_additional_document_request'],
    async (payload: PostAdditionalDocumentRequestBody['data']['attributes']) => {
      const response = await request<PostAdditionalDocumentRequestResponse>(
        `${config.apiHost}/private/v1/applications/${id}/additional_document_requests`,
        {
          method: 'POST',
          body: {
            data: {
              type: 'application_additional_document_request',
              attributes: payload,
            },
          } as PostAdditionalDocumentRequestBody,
          isExpectedResponse,
        }
      );

      const req = response.data;
      const attrs = req.attributes;

      queryClient.invalidateQueries(['application', id]);

      return {
        ...attrs,
        requestedAt: new Date(attrs.requestedAt),
        id: req.id,
      } as AdditionalDocumentsRequest;
    }
  );

  return {
    postAdditionalDocumentRequest: mutateAsync,
    postAdditionalDocumentRequestError: error,
    isPostingAdditionalDocumentRequest: isLoading,
  };
}

export function useGetCustomizedDocumentTypes() {
  const request = useRequest();

  const { data, error, isLoading } = useQuery(
    ['customized_document_types'], // Unique query key
    async () => {
      const response = await request<GetCustomizedDocumentTypesResponse>(
        `${config.apiHost}/private/v1/customized_document_types`,
        {
          method: 'GET',
          isExpectedResponse: isExpectedGetCustomizedDocumentTypesResponse,
        }
      );
      return response;
    }
  );

  return {
    customizedDocumentTypes: data?.data,
    getCustomizedDocumentTypesError: error,
    isFetchingCustomizedDocumentTypes: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is PostAdditionalDocumentRequestResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}

function isExpectedGetCustomizedDocumentTypesResponse(res: unknown): res is GetCustomizedDocumentTypesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
